import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { Loading, Panel } from '../ui/Page';
import { listCatalogue, listTags } from '../settings/catalogues-api';
import { BlockList } from './BlockEditor';
import { TemplatePicker } from './TemplatePicker';
import { addBlock, moveBlock, problemsIn, removeBlock, setProp } from './blocks';
import { listBlocks, type PageContent } from './pages-api';
import {
  createCampaign,
  listSegments,
  previewCampaign,
  scheduleCampaign,
  setCampaignWindow,
  type AudienceFilter,
  type CampaignPreview,
  type SendWindow,
} from './campaigns-api';

/**
 * Creating a campaign, in four steps (context.md sections 45, 46).
 *
 * A wizard rather than one long form, because the four decisions are genuinely sequential: what the
 * message is, who gets it, when it may go, and then — before anything is committed — what that
 * actually adds up to. The review step is the point of the whole shape: it is the last moment
 * somebody can see that their audience is four thousand people rather than forty.
 *
 * Nothing is created until the last step. A wizard that writes a draft at step one leaves half-built
 * campaigns behind every time somebody changes their mind.
 */

type Step = 1 | 2 | 3 | 4;

const WEEKDAYS: { value: number; key: string }[] = [
  { value: 1, key: 'mon' },
  { value: 2, key: 'tue' },
  { value: 3, key: 'wed' },
  { value: 4, key: 'thu' },
  { value: 5, key: 'fri' },
  { value: 6, key: 'sat' },
  { value: 7, key: 'sun' },
];

export function CampaignWizard(): React.JSX.Element {
  const navigate = useNavigate();
  const client = useQueryClient();

  const [step, setStep] = useState<Step>(1);
  const [error, setError] = useState<string | null>(null);

  // Step 1 — what the message is.
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [fromName, setFromName] = useState('');
  const [body, setBody] = useState('');

  /*
   * Two ways to write the message, and they do not mix.
   *
   * Plain text is what a short announcement wants and it arrives everywhere. Blocks are the same
   * ones the page builder uses, rendered to inline-styled HTML with a text half derived from the
   * same blocks — so both parts of the multipart message say the same thing.
   */
  const [compose, setCompose] = useState<'text' | 'blocks'>('text');
  const [content, setContent] = useState<PageContent>({ blocks: [] });

  const palette = useQuery({
    queryKey: ['page-blocks'],
    queryFn: listBlocks,
    staleTime: 60 * 60 * 1000,
  });

  /* A form is a page's job and a video does not play in an inbox; neither is offered here. */
  const emailPalette = (palette.data?.blocks ?? []).filter(
    (entry) => entry.type !== 'FORM' && entry.type !== 'VIDEO',
  );

  const blockProblems = problemsIn(content.blocks, emailPalette);

  // Step 2 — who gets it.
  const [audience, setAudience] = useState<'ALL' | 'FILTER' | 'SEGMENT' | 'LIST'>('ALL');
  const [segmentId, setSegmentId] = useState('');
  const [addresses, setAddresses] = useState('');
  const [filter, setFilter] = useState<AudienceFilter>({});

  // Step 3 — when it may go.
  const [timing, setTiming] = useState<'NOW' | 'WINDOW'>('NOW');
  const [window, setWindow] = useState<SendWindow>({ days: [1, 2, 3, 4, 5] });
  const [startAt, setStartAt] = useState('');

  // Step 4 — what it adds up to.
  const [preview, setPreview] = useState<CampaignPreview | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const segments = useQuery({ queryKey: ['segments'], queryFn: listSegments });
  const statuses = useQuery({
    queryKey: ['catalogue', 'customer-statuses'],
    queryFn: () => listCatalogue('customer-statuses'),
  });
  const sources = useQuery({
    queryKey: ['catalogue', 'customer-sources'],
    queryFn: () => listCatalogue('customer-sources'),
  });
  const tags = useQuery({ queryKey: ['catalogue', 'tags'], queryFn: listTags });

  const recipients = addresses
    .split(/[\n,;]+/)
    .map((value) => value.trim())
    .filter(Boolean);

  /** The campaign is written once, when somebody asks to see what it would do. */
  const build = useMutation({
    mutationFn: async () => {
      const campaign = await createCampaign({
        name: name.trim(),
        subject: subject.trim(),
        ...(compose === 'blocks' ? { content } : { bodyText: body }),
        fromName: fromName.trim() || null,
        ...(audience === 'SEGMENT'
          ? { segmentId }
          : audience === 'LIST'
            ? { recipients }
            : { filter: audience === 'ALL' ? {} : filter }),
      });

      if (timing === 'WINDOW') await setCampaignWindow(campaign.id, window);

      return { campaign, preview: await previewCampaign(campaign.id) };
    },
    onSuccess: (result) => {
      setCreatedId(result.campaign.id);
      setPreview(result.preview);
      setError(null);
      setStep(4);
      void client.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const send = useMutation({
    mutationFn: () => scheduleCampaign(createdId!, startAt || undefined),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['campaigns'] });
      navigate('/marketing/campanhas');
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const canContinue =
    step === 1
      ? name.trim() !== '' &&
        subject.trim() !== '' &&
        (compose === 'blocks'
          ? content.blocks.length > 0 && blockProblems.length === 0
          : body.trim() !== '')
      : step === 2
        ? audience !== 'SEGMENT' || segmentId !== ''
        : true;

  const toggle = (list: string[] | undefined, id: string): string[] => {
    const current = list ?? [];

    return current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id];
  };

  return (
    <div className="view">
      <header className="wizard__head">
        <h1>{t('wizard.title')}</h1>
        <p className="muted">{t('wizard.description')}</p>
      </header>

      <ol className="wizard__steps">
        {([1, 2, 3, 4] as Step[]).map((value) => (
          <li
            key={value}
            className={`wizard__step${value === step ? ' wizard__step--current' : ''}${
              value < step ? ' wizard__step--done' : ''
            }`}
          >
            <span className="wizard__mark">{value}</span>
            <span>{t(`wizard.step.${value}`)}</span>
          </li>
        ))}
      </ol>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {step === 1 && (
        <Panel title={t('wizard.step.1')}>
          <div className="form-grid">
            <label className="field">
              <span>{t('wizard.name')} *</span>
              <input value={name} maxLength={160} onChange={(e) => setName(e.target.value)} />
              <span className="muted small">{t('wizard.nameHint')}</span>
            </label>

            <label className="field">
              <span>{t('wizard.fromName')}</span>
              <input
                value={fromName}
                maxLength={120}
                onChange={(e) => setFromName(e.target.value)}
              />
              <span className="muted small">{t('wizard.fromNameHint')}</span>
            </label>
          </div>

          <label className="field">
            <span>{t('wizard.subject')} *</span>
            <input value={subject} maxLength={300} onChange={(e) => setSubject(e.target.value)} />
            <span className="muted small">{t('wizard.subjectHint')}</span>
          </label>

          <div className="field">
            <span>{t('wizard.body')} *</span>

            <nav className="tabs tabs--compact" aria-label={t('wizard.body')}>
              <button
                type="button"
                className={`tab${compose === 'text' ? ' is-active' : ''}`}
                onClick={() => setCompose('text')}
              >
                {t('wizard.plainText')}
              </button>
              <button
                type="button"
                className={`tab${compose === 'blocks' ? ' is-active' : ''}`}
                onClick={() => setCompose('blocks')}
              >
                {t('wizard.blocks')}
              </button>
            </nav>

            {compose === 'text' ? (
              <>
                <textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
                <span className="muted small">{t('campaigns.bodyHint')}</span>
              </>
            ) : (
              <>
                <span className="muted small">{t('wizard.blocksHint')}</span>

                {content.blocks.length === 0 && (
                  <TemplatePicker kind="EMAIL" onPick={setContent} onError={setError} />
                )}

                <BlockList
                  blocks={content.blocks}
                  path={[]}
                  palette={emailPalette}
                  problems={blockProblems}
                  onAdd={(into, type) => setContent(addBlock(content, into, type))}
                  onSet={(at, key, value) => setContent(setProp(content, at, key, value))}
                  onRemove={(at) => setContent(removeBlock(content, at))}
                  onMove={(at, direction) => setContent(moveBlock(content, at, direction))}
                />
              </>
            )}
          </div>
        </Panel>
      )}

      {step === 2 && (
        <Panel title={t('wizard.step.2')}>
          <div className="stack">
            {(['ALL', 'FILTER', 'SEGMENT', 'LIST'] as const).map((value) => (
              <label key={value} className="field field--inline">
                <input
                  type="radio"
                  name="audience"
                  checked={audience === value}
                  onChange={() => setAudience(value)}
                />
                <span>
                  <span className="strong">{t(`wizard.audience.${value}`)}</span>
                  <span className="muted small"> — {t(`wizard.audienceHint.${value}`)}</span>
                </span>
              </label>
            ))}
          </div>

          {audience === 'SEGMENT' && (
            <label className="field">
              <span>{t('campaigns.segment')}</span>
              <select
                className="select"
                value={segmentId}
                onChange={(e) => setSegmentId(e.target.value)}
              >
                <option value="">{t('wizard.chooseSegment')}</option>
                {(segments.data?.segments ?? []).map((segment) => (
                  <option key={segment.id} value={segment.id}>
                    {segment.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {audience === 'LIST' && (
            <label className="field">
              <span>{t('campaigns.addresses')}</span>
              <textarea rows={4} value={addresses} onChange={(e) => setAddresses(e.target.value)} />
              <span className="muted small">
                {t('sms.recipientCount').replace('{n}', String(recipients.length))}
              </span>
            </label>
          )}

          {audience === 'FILTER' && (
            <div className="stack">
              <fieldset className="field">
                <legend>{t('catalogues.tab.statuses')}</legend>
                <div className="chips">
                  {(statuses.data ?? []).map((status) => (
                    <button
                      key={status.id}
                      type="button"
                      className={`chip${filter.statusIds?.includes(status.id) ? ' chip--active' : ''}`}
                      onClick={() =>
                        setFilter({ ...filter, statusIds: toggle(filter.statusIds, status.id) })
                      }
                    >
                      {status.name}
                    </button>
                  ))}
                </div>
              </fieldset>

              <fieldset className="field">
                <legend>{t('catalogues.tab.sources')}</legend>
                <div className="chips">
                  {(sources.data ?? []).map((source) => (
                    <button
                      key={source.id}
                      type="button"
                      className={`chip${filter.sourceIds?.includes(source.id) ? ' chip--active' : ''}`}
                      onClick={() =>
                        setFilter({ ...filter, sourceIds: toggle(filter.sourceIds, source.id) })
                      }
                    >
                      {source.name}
                    </button>
                  ))}
                </div>
              </fieldset>

              <fieldset className="field">
                <legend>{t('catalogues.tab.tags')}</legend>
                <div className="chips">
                  {(tags.data?.tags ?? []).map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      className={`chip${filter.tagIds?.includes(tag.id) ? ' chip--active' : ''}`}
                      onClick={() =>
                        setFilter({ ...filter, tagIds: toggle(filter.tagIds, tag.id) })
                      }
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              </fieldset>

              <div className="form-grid">
                <label className="field">
                  <span>{t('wizard.createdFrom')}</span>
                  <input
                    type="date"
                    value={filter.createdFrom ?? ''}
                    onChange={(e) =>
                      setFilter({ ...filter, createdFrom: e.target.value || undefined })
                    }
                  />
                </label>

                <label className="field">
                  <span>{t('wizard.createdTo')}</span>
                  <input
                    type="date"
                    value={filter.createdTo ?? ''}
                    onChange={(e) =>
                      setFilter({ ...filter, createdTo: e.target.value || undefined })
                    }
                  />
                </label>
              </div>
            </div>
          )}
        </Panel>
      )}

      {step === 3 && (
        <Panel title={t('wizard.step.3')}>
          <div className="stack">
            <label className="field field--inline">
              <input
                type="radio"
                name="timing"
                checked={timing === 'NOW'}
                onChange={() => setTiming('NOW')}
              />
              <span>
                <span className="strong">{t('wizard.timing.NOW')}</span>
                <span className="muted small"> — {t('wizard.timingHint.NOW')}</span>
              </span>
            </label>

            <label className="field field--inline">
              <input
                type="radio"
                name="timing"
                checked={timing === 'WINDOW'}
                onChange={() => setTiming('WINDOW')}
              />
              <span>
                <span className="strong">{t('wizard.timing.WINDOW')}</span>
                <span className="muted small"> — {t('wizard.timingHint.WINDOW')}</span>
              </span>
            </label>
          </div>

          {timing === 'WINDOW' && (
            <div className="stack">
              <fieldset className="field">
                <legend>{t('wizard.weekdays')}</legend>
                <div className="chips">
                  {WEEKDAYS.map((day) => (
                    <button
                      key={day.value}
                      type="button"
                      className={`chip${window.days?.includes(day.value) ? ' chip--active' : ''}`}
                      onClick={() =>
                        setWindow({
                          ...window,
                          days: window.days?.includes(day.value)
                            ? window.days.filter((value) => value !== day.value)
                            : [...(window.days ?? []), day.value],
                        })
                      }
                    >
                      {t(`wizard.weekday.${day.key}`)}
                    </button>
                  ))}
                </div>
              </fieldset>

              <div className="form-grid">
                <label className="field">
                  <span>{t('wizard.from')}</span>
                  <input
                    type="time"
                    value={window.from ?? ''}
                    onChange={(e) => setWindow({ ...window, from: e.target.value || undefined })}
                  />
                </label>

                <label className="field">
                  <span>{t('wizard.to')}</span>
                  <input
                    type="time"
                    value={window.to ?? ''}
                    onChange={(e) => setWindow({ ...window, to: e.target.value || undefined })}
                  />
                </label>

                <label className="field">
                  <span>{t('wizard.until')}</span>
                  <input
                    type="date"
                    value={window.until ?? ''}
                    onChange={(e) => setWindow({ ...window, until: e.target.value || undefined })}
                  />
                </label>
              </div>

              <p className="muted small">{t('wizard.windowHint')}</p>
            </div>
          )}

          <label className="field">
            <span>{t('wizard.startAt')}</span>
            <input
              type="datetime-local"
              value={startAt}
              onChange={(e) => setStartAt(e.target.value)}
            />
            <span className="muted small">{t('wizard.startAtHint')}</span>
          </label>
        </Panel>
      )}

      {step === 4 && (
        <Panel title={t('wizard.step.4')}>
          {build.isPending || !preview ? (
            <Loading />
          ) : (
            <>
              <dl className="summary">
                <div>
                  <dt>{t('wizard.audienceSize')}</dt>
                  <dd className="strong">{preview.members}</dd>
                </div>
                <div>
                  <dt>{t('wizard.willReceive')}</dt>
                  <dd className="strong">{preview.sendable}</dd>
                </div>
                <div>
                  <dt>{t('campaigns.previewSkipped').split(',')[0]}</dt>
                  <dd>{preview.suppressed}</dd>
                </div>
                <div>
                  <dt>{t('wizard.withoutConsent')}</dt>
                  <dd>{preview.without_consent}</dd>
                </div>
              </dl>

              <p className="muted small">{t('wizard.reviewHint')}</p>

              {preview.sendable === 0 && (
                <p className="alert" role="alert">
                  {t('wizard.nobody')}
                </p>
              )}
            </>
          )}
        </Panel>
      )}

      <div className="wizard__actions">
        <button
          type="button"
          className="button button--quiet"
          disabled={step === 1 || build.isPending}
          onClick={() => setStep((current) => (current - 1) as Step)}
        >
          ← {t('wizard.back')}
        </button>

        <button
          type="button"
          className="button button--quiet"
          onClick={() => navigate('/marketing/campanhas')}
        >
          {t('sms.cancel')}
        </button>

        {step < 3 && (
          <button
            type="button"
            className="button button--primary"
            disabled={!canContinue}
            onClick={() => setStep((current) => (current + 1) as Step)}
          >
            {t('wizard.next')} →
          </button>
        )}

        {step === 3 && (
          <button
            type="button"
            className="button button--primary"
            disabled={build.isPending}
            onClick={() => build.mutate()}
          >
            {build.isPending ? t('work.creating') : t('wizard.review')} →
          </button>
        )}

        {step === 4 && (
          <button
            type="button"
            className="button button--primary"
            disabled={!createdId || send.isPending || preview?.sendable === 0}
            onClick={() => send.mutate()}
          >
            {send.isPending ? t('work.creating') : t('wizard.confirm')}
          </button>
        )}
      </div>
    </div>
  );
}
